import { FC, useCallback, useEffect, useState } from 'react';
import { Field, Formatter, Normalizer } from 'redux-form';

import ThemedFormInput from 'inquiry/components/ThemedFormInput';
import { parseEmptyStringToNull } from 'inquiry/lib/form';
import { useApply, useValidators } from 'inquiry/lib/jsonLogic';
import { InputNumberComponent } from 'lib/interfaces/ui-components';
import isEmpty from 'lib/utils/formValidation/isEmpty';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

interface InputNumber {
  component: InputNumberComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
}

const FLOATING_POINT_REGEX = /^-?\d*\.?\d*$/;
const NUMBER_REGEX = /\d+/g;

function isNumeric(str: string) {
  return FLOATING_POINT_REGEX.test(str);
}

const InputNumber: FC<InputNumber> = ({ component, inquiry, actionsApi }: InputNumber) => {
  const {
    disabled,
    hidden,
    label,
    optional,
    placeholder,
    prefill,
    validations = [],
    precision,
  } = component.attributes;
  const formField = component.name;
  const apply = useApply(formField, inquiry.attributes['next-step'].name);
  const validators = useValidators({ hidden, optional, validations });

  const isHidden = apply<boolean>(hidden);

  // For intermediate non-numeric values, we want to let the user keep typing but
  // not update the store. See normalize and format below
  const [tempDisplayValue, setTempDisplayValue] = useState<string | null>(null);

  useEffect(() => {
    if (isHidden) {
      // Setting to '' removes the field value from form state entirely, unlike null which sets value to null.
      actionsApi.setField(formField, '');
    } else if (!isHidden && !isEmpty(prefill)) {
      actionsApi.setField(formField, prefill);
    }

    // Including actionsApi here results in value resetting on
    // prefill and blur
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidden]);

  /**
   * NOTE: normalize() and format() below ensure user input is
   * always numeric, and within ranges we can parse without errors.
   *
   *  (Number)                     (String)
   *      ┌─────────normalize()◄─────┐
   *      │                          │
   *      ▼                          │
   * Redux Store               <Input ... />
   *      │                          ▲
   *      │                          │
   *      └──────────►format()───────┘
   *   (Number)                 (Number|String)
   */

  // HTMLInput's deal in strings, but we want either an int
  // or float sent to the server
  const normalize = useCallback<Normalizer>((value, priorvalue) => {
    // This ensures empty inputs (which are empty strings) are sent to the server
    // as null, so optional fields may unset the value
    if (value == null || !value.trim()) {
      setTempDisplayValue(null);
      return null;
    }

    if (!isNumeric(value)) {
      return priorvalue;
    }

    const [integer, decimals] = value.match(NUMBER_REGEX) ?? [];

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (decimals?.length > precision) {
      return priorvalue;
    }

    const nums = [integer, decimals].join('');

    // values having more than 16 total digits won't return
    // the expected value on calls to parseFloat
    if (nums.length > 16) return priorvalue;

    // avoid displaying parseFloat on intermediate results, e.g.
    // 5. and 5.0, which immediately parseFloat to 5 but
    // might become 5.05
    if (value.endsWith('.') || value.endsWith('0')) {
      setTempDisplayValue(value);
    } else if (value === '-') {
      setTempDisplayValue(value);
      return priorvalue;
    } else {
      setTempDisplayValue(null);
    }

    const parsed = parseFloat(tempDisplayValue === '-' ? -value : value);

    // numbers below this result in Scientific notation, e.g 1e-7 or 1.591e-9, etc
    // this rounds them up to 0.000001, the smallest number that would NOT
    // result in scientific notation (in testing)
    if (0 < parsed && parsed < 0.000001) {
      return 0.000001;
    } else {
      return parsed;
    }
  }, []);

  // Format numeric value for display, optionally showing
  // the local copy for partially entered values (ex: 5.)
  const format = useCallback<Formatter>(
    (value) => {
      if (tempDisplayValue != null) {
        return tempDisplayValue;
      }

      // NOTE: Without format defined, redux-form would do this for us
      if (value == null) return '';
      return value;
    },
    [tempDisplayValue],
  );

  if (isHidden) {
    return null;
  }

  return (
    <Field
      normalize={normalize}
      format={format}
      name={formField}
      id={formField}
      component={ThemedFormInput}
      disabled={apply<boolean>(disabled)}
      parse={parseEmptyStringToNull}
      validate={validators}
      label={label}
      placeholder={placeholder}
      type='text'
      inputmode='decimal'
      styles={component.styles}
    />
  );
};

export default InputNumber;
